<!--
  商品详情页
  用途：从 API 获取商品并展示图片/视频、规格、交期等
  用法：路由 `/product/:id`
-->
<template>
  <div class="detail-page">
    <p v-if="loading" class="state">{{ t('common.loading') }}</p>
    <p v-else-if="error" class="state">{{ error }}</p>

    <div v-else-if="product" class="detail-inner">
      <button class="back-btn" type="button" @click="goBack">{{ t('product.backList') }}</button>

      <div class="detail-grid">
        <MediaGallery
          class="gallery"
          :items="product.gallery"
          :fallback="product.src"
          :alt="product.name"
        />

        <div class="info">
          <span class="category">{{ product.category }}</span>
          <h1>{{ product.name }}</h1>
          <p class="price">{{ product.price }}</p>
          <p class="desc">{{ product.description }}</p>

          <ul class="meta-list">
            <li><strong>{{ t('product.moq') }}</strong>{{ product.moq }}</li>
            <li><strong>{{ t('product.material') }}</strong>{{ product.material }}</li>
            <li><strong>{{ t('product.leadTime') }}</strong>{{ product.leadTime }}</li>
          </ul>

          <div class="spec-tags">
            <span v-for="(spec, index) in product.specs" :key="index">{{ spec }}</span>
          </div>

          <div class="actions">
            <router-link class="btn btn-primary" to="/contact">{{ t('product.inquire') }}</router-link>
            <router-link class="btn btn-ghost" to="/">{{ t('product.keepBrowsing') }}</router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="empty">
      <img class="empty-logo" :src="logoUrl" :alt="t('common.brandLogoAlt')" />
      <h2>{{ t('product.notFoundTitle') }}</h2>
      <p>{{ t('product.notFoundDesc') }}</p>
      <router-link class="btn btn-primary" to="/">{{ t('common.backHome') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import MediaGallery from '@/components/MediaGallery.vue'
import { fetchCategories, fetchProduct } from '@/services/api'
import { localizeProductFromApi } from '@/utils/localize'
import logoUrl from '@/assets/logo.png'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()

const raw = ref(null)
const categories = ref([])
const loading = ref(true)
const error = ref('')

const product = computed(() =>
  localizeProductFromApi(raw.value, locale.value, t, categories.value)
)

const load = async () => {
  loading.value = true
  error.value = ''
  raw.value = null
  try {
    const [productData, categoryData] = await Promise.all([
      fetchProduct(route.params.id),
      fetchCategories()
    ])
    raw.value = productData
    categories.value = categoryData
  } catch (e) {
    if (e.status === 404) {
      raw.value = null
    } else {
      error.value = e.message || t('common.loadFailed')
    }
  } finally {
    loading.value = false
  }
}

watch(() => route.params.id, load, { immediate: true })

const goBack = () => {
  if (window.history.length > 1) router.back()
  else router.push('/')
}
</script>

<style scoped>
.detail-page {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 28px 24px 64px;
}

.state {
  padding: 48px 16px;
  text-align: center;
  color: var(--color-text-secondary);
}

.back-btn {
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 14px;
  margin-bottom: 20px;
  cursor: pointer;
}

.back-btn:hover {
  color: var(--color-primary);
}

.detail-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 40px;
  align-items: start;
}

.category {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
}

.info h1 {
  font-size: clamp(26px, 3vw, 34px);
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
}

.price {
  color: var(--color-primary);
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 16px;
}

.desc {
  color: var(--color-text-secondary);
  line-height: 1.75;
  margin-bottom: 20px;
}

.meta-list {
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
  padding: 16px;
  border-radius: 12px;
  background: var(--color-bg-muted);
}

.meta-list li {
  font-size: 14px;
  color: var(--color-text);
}

.spec-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
}

.spec-tags span {
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-border);
  font-size: 13px;
  color: var(--color-text-secondary);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 128px;
  padding: 12px 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-primary {
  background: var(--color-primary);
  color: #fff;
}

.btn-primary:hover {
  background: var(--color-primary-hover);
}

.btn-ghost {
  background: #fff;
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-ghost:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.empty {
  text-align: center;
  padding: 80px 16px;
}

.empty-logo {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}

.empty h2 {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty p {
  color: var(--color-text-secondary);
  margin-bottom: 20px;
}

@media (max-width: 860px) {
  .detail-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .detail-page {
    padding: 20px 16px 48px;
  }
}
</style>
